import { Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StewartFunctions } from '@functions/stewart.service';
import { PropertyType } from '@models/matter';
import { PropertyUsage } from '@models/title-insurance';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select, Store } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import LogRocket from 'logrocket';
import { combineLatest, firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Step } from 'src/app/shared/atomic/molecules/step-progress-nav/step-progress-nav.component';
import { ICON } from '@core/util/icon';
import { environment } from 'src/environments/environment';
import { AddTitleInsuranceComponent } from '../components/add-title-insurance/add-title-insurance.component';
import {
	MissingFieldSection,
	ValidateStewartDataService,
} from './services/validate-stewart-data.service';
import { StewartModalComponent } from './stewart-modal/stewart-modal.component';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import {
	MatterStewartTitleInsurance,
	StewartStatus,
} from '@models/title-insurance/stewart';
import { BlankTitleInsurance } from '../chicago/chicago.component';
import {
	StewartCancelModalComponent,
	StewartCancelModalData,
} from './components/cancel-modal/cancel-modal.component';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { StewartSelectors } from '@state/global/title-insurance/stewart';
import { checkForDuplicatePriority } from '@core/util/title-insurance';
import { ActivatedRoute } from '@angular/router';
import { BusinessState } from '@state/business';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { isRegionOntario } from '@core/util/address.utils';
import { TestHooks } from './stewart.hooks';

const Steps: Step[] = [
	{ title: 'Preparing' },
	{ title: 'Submitted' },
	{ title: 'Processing' },
	{ title: 'Completed' },
];

@UntilDestroy()
@Component({
	selector: 'app-stewart',
	templateUrl: './stewart.component.html',
	styleUrls: ['./stewart.component.scss'],
	standalone: false,
})
export class StewartComponent implements OnInit {
	testHooks = TestHooks;
	private store = inject(Store);
	public allowSwitchProviders = false;
	public blankTitleInsurance = BlankTitleInsurance;
	public canCancel = true;
	public canSubmit = false;
	public changePropertyButtonText = '';
	public currentStep = 0;
	public ICON = ICON;
	public isOther = false;
	public isMobileHome = false;
	public isCommercial = false;
	public missing: MissingFieldSection[] = [];
	public messages: string[] = [];
	public orderValid = false;
	public status: StewartStatus;
	public StewartStatus = StewartStatus;
	public steps = Steps;
	public titleInsurance: MatterStewartTitleInsurance;
	public outOfOntario = false;
	public showPriorityWarning = false;
	public loading = false;
	public matterTitles = this.store.selectSnapshot(MatterTitlesState.getAll);
	public thirdPartyId = this.store.selectSnapshot(
		SelectedMatter.titleInsuranceThirdPartyId,
	);
	public matterId = this.store.selectSnapshot(SelectedMatter.id);
	public currentPayload = this.store.selectSnapshot(
		StewartSelectors.getStewartDataPayload,
	);
	public previousPayload = this.store.selectSnapshot(
		StewartSelectors.previousPayload,
	);
	public nameFormattingSettings = this.store.selectSnapshot(
		BusinessState.getNameFormattingSettings,
	);
	public state$ = this.store.select(SelectedMatter.getTitleInsuranceState);
	public matter$ = this.store.select(SelectedMatter.getSelected);
	public clients$ = this.store.select(
		MatterClients.get_mortgagorsWithAssociationMapped,
	);

	public otherParty$ = this.store.select(
		MatterClients.get_otherPartyWithAssociationMapped,
	);

	public mortgages$ = this.store.select(
		MatterMortgagesState.getAllMortgagesWithAssociations,
	);
	public remainEncumbrances$ = this.store.select(
		MatterEncumbrancesState.getRemain,
	);
	public effectiveDate$ = this.store.select(
		SelectedMatter.titleInsuranceEffectiveDate,
	);
	titleInsuranceState_ = select(SelectedMatter.getTitleInsuranceState);

	public lawyers$ = this.store.select(BusinessUsersState.getLawyers);

	public loginUrl = `${environment.stewartBaseLoginUrl}?client_id=${environment.stewartClientId}`;

	constructor(
		private route: ActivatedRoute,
		private stewartFunctions: StewartFunctions,
		private validateStewartDataService: ValidateStewartDataService,
		private matDialog: MatDialog,
		private matterDispatch: MatterDispatch,
	) {}

	ngOnInit(): void {
		if (this.titleInsuranceState_()) {
			firstValueFrom(
				this.stewartFunctions.getStatus_v2(
					this.matterId,
					this.thirdPartyId,
					this.previousPayload,
				),
			)
				.catch((error) => {
					LogRocket.log('Error getting Stewart status details', error);
				})
				.finally(() => {
					this.loading = false;
				});
		}

		this.state$.pipe(untilDestroyed(this)).subscribe((_state) => {
			if (_state) {
				this.status = _state as StewartStatus;
				this.setCurrentStep(this.status);
				this.setAllowSwitchProviders(this.status);
				this.changeStepperColor();
				this.canCancel = true;
				if (
					this.status === StewartStatus.DECLINED ||
					this.status === StewartStatus.CANCELLED
				) {
					this.canCancel = false;
					this.steps.pop();
					this.steps.push({
						title: `${this.status === StewartStatus.CANCELLED ? 'Cancelled' : 'Declined'}`,
						failed: true,
					});
					this.changeStepperColor('failed');
				}
				if (
					this.status === StewartStatus.ORDER_RESUBMIT_REQUIRED ||
					(this.status === StewartStatus.NOT_SUBMITTED && !this.orderValid)
				) {
					this.changeStepperColor('warning');
				}
			}
		});

		combineLatest([
			this.matter$,
			this.clients$,
			this.otherParty$,
			this.mortgages$,
			this.remainEncumbrances$,
			this.lawyers$,
		])
			.pipe(untilDestroyed(this))
			.subscribe(
				([
					_matter,
					_clients,
					_otherParty,
					_mortgages,
					_remainEncumbrances,
					_lawyers,
				]) => {
					if (_matter) {
						if (!_matter) {
							return;
						}

						const propertyType = _matter.propertyDetails?.propertyType?.type;
						const propertyUsage = _matter.propertyDetails?.propertyType?.usage;

						this.isCommercial = propertyUsage === PropertyUsage.COMMERCIAL;

						this.outOfOntario = !isRegionOntario(
							_matter?.propertyDetails?.address?.province,
						);

						this.isOther =
							propertyUsage === PropertyUsage.RESIDENTIAL &&
							propertyType === PropertyType.OTHER;
						this.isMobileHome =
							propertyUsage === PropertyUsage.RESIDENTIAL &&
							propertyType === PropertyType.MOBILE_HOME;

						this.showPriorityWarning = !!checkForDuplicatePriority(
							_mortgages,
							_remainEncumbrances,
						);

						const missingOrderFields =
							this.validateStewartDataService.getSubmitDataMissingSections(
								_matter,
								_clients,
								_otherParty,
								_mortgages,
								_remainEncumbrances,
								_lawyers,
								this.matterTitles,
								this.nameFormattingSettings,
							);

						this.orderValid =
							missingOrderFields?.length === 0 &&
							!this.isCommercial &&
							!this.isOther &&
							!this.isMobileHome &&
							!this.outOfOntario;
						this.missing = missingOrderFields;

						this.canSubmit = !(
							this.isCommercial ||
							this.isOther ||
							this.isMobileHome ||
							this.outOfOntario
						);
						if (this.isCommercial || this.isOther || this.isMobileHome) {
							this.changePropertyButtonText = 'Change property type';
						} else if (this.outOfOntario) {
							this.changePropertyButtonText = 'Change property address';
						}

						this.titleInsurance =
							_matter.titleInsurance as MatterStewartTitleInsurance;
						this.messages = this.titleInsurance?.messages;
					}
				},
			);

		this.route.queryParams
			.pipe(untilDestroyed(this))
			.subscribe((queryparams) => {
				const { modal } = queryparams;

				if (modal === 'submit') {
					this.submitFile();
				}
			});
	}

	setCurrentStep(status: StewartStatus): void {
		switch (status) {
			case StewartStatus.NOT_SUBMITTED:
				this.currentStep = 0;
				break;
			case StewartStatus.ORDER_RESUBMIT_REQUIRED:
				this.currentStep = 2;
				break;
			case StewartStatus.FLAGGED:
				this.currentStep = 2;
				break;
			case StewartStatus.CANCELLED:
				this.currentStep = 3;
				break;
			case StewartStatus.DECLINED:
				this.currentStep = 3;
				break;
			case StewartStatus.ORDERED:
				this.currentStep = 3;
				break;
			default:
				this.currentStep = 1;
				break;
		}
	}

	changeStepperColor(state?: string): void {
		switch (state) {
			case 'failed':
				this.steps = this.steps.map((step) => ({
					...step,
					failed: true,
					warning: false,
				}));
				break;
			case 'warning':
				this.steps = this.steps.map((step) => ({
					...step,
					failed: false,
					warning: true,
				}));
				break;
			default:
				this.steps = this.steps.map((step) => ({
					...step,
					failed: false,
					warning: false,
				}));
				break;
		}
	}

	submitFile() {
		if (this.status === StewartStatus.DECLINED) {
			//show details
			return;
		}
		this.matDialog.open(StewartModalComponent);
	}

	selectInsuranceProvider() {
		this.matDialog.open(AddTitleInsuranceComponent);
	}

	cancel(): void {
		const data: StewartCancelModalData = {
			status: this.status,
		};

		this.matDialog
			.open(StewartCancelModalComponent, {
				data,
				maxWidth: '450px',
				width: '90%',
			})
			.afterClosed()
			.pipe(
				untilDestroyed(this),
				take(1),
				filter((result) => !!result),
			)
			.subscribe(() => {
				if (!this.status || this.status === StewartStatus.NOT_SUBMITTED) {
					this.matterDispatch.SetTitleInsurance(
						this.matterId,
						this.blankTitleInsurance,
					);
				} else {
					this.matDialog.open(StewartModalComponent);
				}
			});
	}

	setAllowSwitchProviders(status: StewartStatus) {
		this.allowSwitchProviders = [
			StewartStatus.NOT_SUBMITTED,
			StewartStatus.CANCELLED,
			StewartStatus.DECLINED,
		].includes(status);
	}
}
